import { createContext, useContext } from "react";
import { mapStrings } from "../lib/frTypo";

/**
 * On-screen copy, FR + EN. The landing is bilingual (frak.id serves both
 * from `/`), so the video ships in both languages from the same timeline.
 *
 * Sourced from the landing itself so the video and the page tell the same
 * story:
 *   - hero / steps / FAQ          → src/i18n/locales/{en,fr}.json
 *   - "brands that pay…"          → brands.hub.title
 *   - "no followers" / "cash, not points" → /vs (LTK, Honey, ReferralCandy)
 *   - 88 % stat                   → blog "nous-sommes-tous-des-influenceurs"
 *                                   (Nielsen, Trust in Advertising 2021)
 *
 * House style (same as the site): tutoiement in FR, no em dashes, no hype
 * words, amounts qualified as examples.
 */

export type Lang = "fr" | "en";

export type Copy = {
  hook: {
    incoming1: string;
    incoming2: string;
    reply: string;
    linkTitle: string;
    headline: string;
    /** Index of the word painted Frak blue. */
    headlineAccent: number;
    status: string;
  };
  trust: {
    stat: number;
    statSuffix: string;
    line: string;
    kicker: string;
    source: string;
  };
  unpaid: {
    lead: string;
    zero: string;
    withFrak: string;
    after: string;
    footnote: string;
  };
  followers: {
    lead: string;
    struck: string;
    answer: string;
  };
  steps: {
    pick: string;
    share: string;
    shareSub: string;
    earn: string;
    friendLabel: string;
    friendValue: string;
    youLabel: string;
    youValue: string;
  };
  notification: {
    app: string;
    time: string;
    title: string;
    body: string;
  };
  cash: {
    line1: string;
    line2: string;
    struck: string;
    walletName: string;
    walletMeta: string;
    button: string;
    done: string;
  };
  brands: { title: string };
  /** Labels of the coded app screens (Explorer + wallet). */
  app: {
    explorer: string;
    brandLine: string;
    wallet: string;
    totalBalance: string;
    transfer: string;
    pending: string;
    allTime: string;
    history: string;
    seeAll: string;
    justNow: string;
    /** Dates of the older history rows, newest first. */
    dates: string[];
    tabs: [string, string, string];
  };
  /** "Jusqu'à 6 €" / "Up to €6" — rewards are a max, like on /brands. */
  upTo: (amount: number) => string;
  cta: {
    line1: string;
    line2: string;
    sub: string;
    appStoreSmall: string;
    appStore: string;
    playSmall: string;
    play: string;
  };
  /** Formats a euro amount the local way: 5 → "5,00 €" / "€5.00". */
  money: (value: number, decimals?: number) => string;
};

const frRaw: Copy = {
  hook: {
    incoming1: "Il est trop beau ton sac 😍",
    incoming2: "Tu l'as eu où ??",
    reply: "Ici ! Je l'adore",
    linkTitle: "Sac cabas noir",
    headline: "Tu recommandes déjà.",
    headlineAccent: 2,
    status: "en ligne",
  },
  trust: {
    stat: 88,
    statSuffix: " %",
    line: "font confiance à la recommandation d'un proche.",
    kicker: "Plus qu'à n'importe quelle pub.",
    source: "Source : Nielsen, Trust in Advertising, 2021",
  },
  unpaid: {
    lead: "Et pourtant, tu gagnes",
    zero: "0,00 €",
    withFrak: "Avec",
    after: "dès qu'un proche achète via ton lien.",
    footnote: "Exemple. Le montant dépend de la marque.",
  },
  followers: {
    lead: "Pas besoin de",
    struck: "100k abonnés.",
    answer: "Tes proches suffisent.",
  },
  steps: {
    pick: "Choisis une marque que tu aimes",
    share: "Partage ton lien",
    shareSub: "Story, WhatsApp, SMS…",
    earn: "Ton ami achète. Vous gagnez tous les deux.",
    friendLabel: "Ton ami",
    friendValue: "du cashback",
    youLabel: "Toi",
    youValue: "+{gain}",
  },
  notification: {
    app: "FRAK",
    time: "maintenant",
    title: "Tu viens de gagner {gain} ! 🎉",
    body: "Un achat a été réalisé via ton lien. {gain2} ont été ajoutés à ton porte-monnaie.",
  },
  cash: {
    line1: "Du vrai cash.",
    line2: "Pas des",
    struck: "points.",
    walletName: "Porte-monnaie Frak",
    walletMeta: "Solde disponible",
    button: "Transférer vers ma banque",
    done: "Envoyé sur ton compte",
  },
  brands: { title: "Des marques qui paient leurs ambassadeurs" },
  app: {
    explorer: "Explorer",
    brandLine: "Du cashback pour tes proches",
    wallet: "Porte-monnaie",
    totalBalance: "Total balance",
    transfer: "Transférer vers ma banque",
    pending: "En attente",
    allTime: "Depuis le début",
    history: "Historique des gains",
    seeAll: "Voir tout",
    justNow: "À l'instant",
    dates: ["Hier, 13:13", "7 mars, 09:41", "5 mars, 17:25", "2 mars, 11:45"],
    tabs: ["Porte-monnaie", "Explorer", "Profil"],
  },
  upTo: (n) => `Jusqu'à ${n}\u00A0€`,
  cta: {
    line1: "Gagne de l'argent",
    line2: "en recommandant ce que tu aimes.",
    sub: "Gratuit. Sans abonnés. Du vrai cash.",
    appStoreSmall: "Télécharger dans",
    appStore: "App Store",
    playSmall: "DISPONIBLE SUR",
    play: "Google Play",
  },
  money: (v, d = 2) => `${v.toFixed(d).replace(".", ",")}\u00A0€`,
};

// French typography (no-break spaces before ! ? : € and %), see lib/frTypo.
const fr: Copy = mapStrings(frRaw);

const en: Copy = {
  hook: {
    incoming1: "Love your bag 😍",
    incoming2: "Where did you get it??",
    reply: "Here! Obsessed with it",
    linkTitle: "Black tote bag",
    headline: "You already recommend.",
    headlineAccent: 1,
    status: "online",
  },
  trust: {
    stat: 88,
    statSuffix: "%",
    line: "of people trust recommendations from people they know.",
    kicker: "More than any ad.",
    source: "Source: Nielsen, Trust in Advertising, 2021",
  },
  unpaid: {
    lead: "And yet, you earn",
    zero: "€0.00",
    withFrak: "With",
    after: "every time a friend buys through your link.",
    footnote: "Example. The amount depends on the brand.",
  },
  followers: {
    lead: "No need for",
    struck: "100k followers.",
    answer: "Your friends are enough.",
  },
  steps: {
    pick: "Pick a brand you love",
    share: "Share your link",
    shareSub: "Story, WhatsApp, text…",
    earn: "Your friend buys. You both win.",
    friendLabel: "Your friend",
    friendValue: "cashback",
    youLabel: "You",
    youValue: "+{gain}",
  },
  notification: {
    app: "FRAK",
    time: "now",
    title: "You just earned {gain}! 🎉",
    body: "A purchase was made through your link. {gain2} has been added to your wallet.",
  },
  cash: {
    line1: "Real cash.",
    line2: "Not",
    struck: "points.",
    walletName: "Frak wallet",
    walletMeta: "Available balance",
    button: "Transfer to my bank",
    done: "Sent to your bank account",
  },
  brands: { title: "Brands that pay their ambassadors" },
  app: {
    explorer: "Explore",
    brandLine: "Cashback for your friends",
    wallet: "Wallet",
    totalBalance: "Total balance",
    transfer: "Transfer to my bank",
    pending: "Pending",
    allTime: "All time",
    history: "Earnings history",
    seeAll: "See all",
    justNow: "Just now",
    dates: ["Yesterday, 1:13 PM", "Mar 7, 9:41 AM", "Mar 5, 5:25 PM", "Mar 2, 11:45 AM"],
    tabs: ["Wallet", "Explore", "Profile"],
  },
  upTo: (n) => `Up to €${n}`,
  cta: {
    line1: "Earn money",
    line2: "by recommending what you love.",
    sub: "Free. No followers needed. Real cash.",
    appStoreSmall: "Download on the",
    appStore: "App Store",
    playSmall: "GET IT ON",
    play: "Google Play",
  },
  money: (v, d = 2) => `€${v.toFixed(d)}`,
};

export const COPY: Record<Lang, Copy> = { fr, en };

/**
 * Fill `{gain}` ("5 €") and `{gain2}` ("5,00 €") with the story's live
 * reward, so the copy can never promise more than the brand pays.
 */
export const fill = (copy: Copy, text: string, gain: number) =>
  text
    .replace("{gain2}", copy.money(gain))
    .replace("{gain}", copy.money(gain, 0));

export const CopyContext = createContext<Copy>(fr);

export const useCopy = (): Copy => useContext(CopyContext);
